import { handleSiteStatus } from './site-status'

const trimTrailingSlash = (value = '') => value.replace(/\/$/, '')

export const apiConfig = {
  // 开发时使用同源路径，由 Vite 转发到配置的远程 PHP API。
  // 验证码图片与登录请求因此共享同一浏览器 Session Cookie。
  apiServer: trimTrailingSlash(import.meta.env.DEV ? '/home' : (import.meta.env.VITE_API_SERVER || 'https://api3.137n.com/home')),
  authServer: trimTrailingSlash(import.meta.env.DEV ? '/auth' : (import.meta.env.VITE_AUTH_SERVER || 'https://api3.137n.com/auth')),
  wsServer: import.meta.env.DEV ? (import.meta.env.VITE_DEV_WS_SERVER || 'wss://api3.137n.com/ws') : (import.meta.env.VITE_WS_SERVER || 'wss://api3.137n.com/ws'),
}

const tokenStorageKey = 'imimx.auth-token'
const crsfStorageKey = 'imimx.crsf-token'
const accessTokenStorageKey = 'imimx.access-token'
const userModelStorageKey = 'AuthUserModel'
const legacyStorageKeys = ['Authorization', 'AuthToken', 'CrsfToken']
const maxTimerDelay = 2147483647
let sessionExpiryTimer
let sessionVersion = 0
let refreshPending = null

function redirectToLogin() {
  if (window.location.pathname === '/login') return

  const loginUrl = new URL('/login', window.location.origin)
  loginUrl.searchParams.set('redirect', `${window.location.pathname}${window.location.search}${window.location.hash}`)
  window.location.replace(loginUrl.toString())
}

function expireSession() {
  authStorage.clear()
  redirectToLogin()
}

function scheduleSessionExpiry(userModel = authStorage.getUserModel()) {
  window.clearTimeout(sessionExpiryTimer)

  const expiresAt = Number(userModel?.token?.expire_time || 0) * 1000
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return

  const remaining = expiresAt - Date.now()
  if (remaining <= 0) {
    expireSession()
    return
  }

  sessionExpiryTimer = window.setTimeout(() => {
    if (Date.now() >= expiresAt) expireSession()
    else scheduleSessionExpiry(userModel)
  }, Math.min(remaining, maxTimerDelay))
}

function getLegacyAccessToken() {
  const directToken = localStorage.getItem('Authorization')
  if (directToken) return directToken
  try {
    return JSON.parse(localStorage.getItem('AuthUserModel') || '{}')?.token?.access_token || ''
  } catch (_) {
    return ''
  }
}

export const authStorage = {
  // These fallbacks mirror the old Angular client's persisted session exactly.
  // They let the replacement client use the same access token for its WebSocket
  // connect frame instead of opening an unauthenticated chat connection.
  getToken: () => localStorage.getItem(tokenStorageKey) || localStorage.getItem('AuthToken') || '',
  getAccessToken: () => localStorage.getItem(accessTokenStorageKey) || getLegacyAccessToken(),
  getCrsfToken: () => localStorage.getItem(crsfStorageKey) || localStorage.getItem('CrsfToken') || '',
  setTokens({ accessToken, authToken, crsfToken }) {
    if (accessToken) localStorage.setItem(accessTokenStorageKey, accessToken)
    if (authToken) localStorage.setItem(tokenStorageKey, authToken)
    if (crsfToken) localStorage.setItem(crsfStorageKey, crsfToken)
  },
  getUserModel: () => {
    try { return JSON.parse(localStorage.getItem(userModelStorageKey) || 'null') } catch (_) { return null }
  },
  setUserModel: (userModel) => {
    if (userModel) {
      sessionVersion += 1
      localStorage.setItem(userModelStorageKey, JSON.stringify(userModel))
      scheduleSessionExpiry(userModel)
    }
  },
  clear: () => {
    sessionVersion += 1
    window.clearTimeout(sessionExpiryTimer)
    localStorage.removeItem(accessTokenStorageKey)
    localStorage.removeItem(tokenStorageKey)
    localStorage.removeItem(crsfStorageKey)
    localStorage.removeItem(userModelStorageKey)
    legacyStorageKeys.forEach((key) => localStorage.removeItem(key))
  },
}

scheduleSessionExpiry()

export async function refreshSession() {
  if (refreshPending) return refreshPending
  const token = authStorage.getUserModel()?.token
  if (!token?.access_token || !token?.refresh_token) return authStorage.getAccessToken()
  if (Number(token.expire_time) * 1000 - Date.now() > 10 * 60 * 1000) return token.access_token
  const version = sessionVersion
  refreshPending = (async () => {
    const response = await fetch(`${apiConfig.authServer}/token/refresh`, {
      method: 'POST', credentials: 'include',
      headers: { 'X-Client': 'web-user', 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: token.access_token, refresh_token: token.refresh_token }),
    })
    const result = await response.json()
    handleSiteStatus(result)
    if (version !== sessionVersion || authStorage.getAccessToken() !== token.access_token) throw new Error('登录状态已变更，请重试')
    if (!response.ok || Number(result.error_code) !== 0) {
      if (response.status === 401 || ['用户未登录', '您还没有登录或登录已超时', '您的账号已在他处登录'].includes(result.error_msg)) expireSession()
      throw new Error(result.error_msg || `请求失败 (${response.status})`)
    }
    const model = result.data
    if (!model?.token?.access_token) throw new Error('登录响应缺少访问令牌')
    authStorage.setTokens({ accessToken: model.token.access_token, authToken: model.token.auth_token, crsfToken: model.token.crsf_token })
    authStorage.setUserModel(model)
    return authStorage.getAccessToken()
  })()
  try { return await refreshPending } finally { refreshPending = null }
}

export async function request(path, { server = 'api', method = 'GET', data, form = false, headers = {}, anonymous = false, signal } = {}) {
  if (!anonymous && server !== 'auth') await refreshSession()
  const baseUrl = server === 'auth' ? apiConfig.authServer : apiConfig.apiServer
  // The old Angular interceptor uses this exact client identity for every
  // auth/home request. Its access token must match the WebSocket's
  // `client: 'web-user'` identity or the server rejects the socket history.
  const requestHeaders = { 'X-Client': 'web-user', ...headers }
  const authToken = authStorage.getToken()
  const accessToken = authStorage.getAccessToken()
  const crsfToken = authStorage.getCrsfToken()

  if (!anonymous && accessToken) {
    requestHeaders['X-Token'] = accessToken
    requestHeaders.Authorization = accessToken
  }
  if (!anonymous && authToken) requestHeaders['X-Auth-Token'] = authToken
  if (!anonymous && crsfToken) requestHeaders['X-Crsf-Token'] = crsfToken

  let url = `${baseUrl}${path}`
  const options = { method, headers: requestHeaders, credentials: 'include', signal }
  if (data && method.toUpperCase() === 'GET') {
    const query = new URLSearchParams(data).toString()
    if (query) url += `?${query}`
  } else if (data) {
    if (form) {
      requestHeaders['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8'
      options.body = new URLSearchParams(data).toString()
    } else {
      requestHeaders['Content-Type'] = 'application/json'
      options.body = JSON.stringify(data)
    }
  }

  const response = await fetch(url, options)
  const result = await response.json()
  handleSiteStatus(result)
  const nextAuthToken = response.headers.get('X-Auth-Token')
  const nextCrsfToken = response.headers.get('X-Crsf-Token')
  authStorage.setTokens({ authToken: nextAuthToken, crsfToken: nextCrsfToken })

  if (!response.ok || result.error_code !== 0) {
    const errorCode = Number(result.error_code)
    if (!anonymous && (errorCode === 1001 || errorCode === 1002)) expireSession()
    throw new Error(result.error_msg || `请求失败 (${response.status})`)
  }
  return result
}
